import Bugsnag from "@bugsnag/js";
import i18n, { type TFunction } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import { WHITELABEL_KEY } from "./globals";
import { warnLog } from "./utils/logger";

export const SUPPORTED_LANGUAGES = [
  "ar",
  "de",
  "en",
  "es-MX",
  "fa",
  "fa-AF",
  "fr",
  "gu-IN",
  "hi",
  "ht",
  "it",
  "km",
  "ko",
  "ne-NP",
  "pl",
  "pt-PT",
  "ru",
  "rw",
  "sm-AS",
  "so",
  "sw",
  "tl",
  "uk",
  "ur-PK",
  "vi",
  "zh-CN",
  "zh-TW",
];

// Figure out what namespace we want to use.
// This allows us to do state-specific string overrides using all standard i18n behavior.
export function getDefaultNS(): string {
  if (WHITELABEL_KEY === "default") {
    return "translation";
  }
  return `${WHITELABEL_KEY}_translation`;
}

/**
 * The namespaces to load: the whitelabel's overrides with the rest. (The React app's hook loaded the
 * default namespace when a component first asked for it; `t` doesn't, so it's loaded up front.)
 */
export function namespaces(): string[] {
  return [...new Set(["translation", getDefaultNS()])];
}

/**
 * The translation function. The language is picked once at startup (there is no in-app language
 * switcher), so components can call this directly without subscribing to changes.
 */
export const t: TFunction = i18n.t.bind(i18n);

export async function initializeI18next(): Promise<TFunction> {
  const missingKeyHandler = (
    lngs: readonly string[],
    ns: string,
    key: string,
    fallbackValue: string,
  ) => {
    const msg = `Could not find symbol ${ns}:${key} for languages ${lngs.join(
      ",",
    )}, fallback value '${fallbackValue}'`;
    warnLog(msg);
    Bugsnag.notify(msg);
  };

  return i18n
    .use(LanguageDetector)
    .use(HttpApi)
    .init({
      detection: {
        // Prevent the app from storing a preferred locale in local storage
        caches: [],
        /**
         * The default is 'i18nextLgn'. Here, we override the lookupLocalStorage key used so that we cleanly ignore any
         * language set in local storage _before_ we added the explicit line above to prevent caching the detected
         * langague in local storage.
         */
        lookupLocalStorage: "i18nextLng_docket",
      },
      // 🥴 The language detector will use navigator.language which looks correct to me, but it decides that
      // en-US is still the best option. Perhaps because it's preloaded? Let's make the language list explicit.
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: "en",
      ns: namespaces(),
      fallbackNS: "translation", // this is the OG, no-namespace version of translations
      defaultNS: getDefaultNS(), // Ta-da, we can get state-specific "namespaces" this way that override default keys
      preload: ["en"],
      // Careful using debug: true on a native device. The massive amount of logging on missing keys can cause
      // scroll lag
      debug: false,
      saveMissing: true,
      missingKeyHandler: missingKeyHandler,
      backend: {
        // something like e.g. '/locales/en/translation.json', or '/locales/en/nj_translation.json'
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
    });
}

export default i18n;
